import { DataProvider } from 'react-admin';
import axios from 'axios';
import { Municipality } from '../administration/type';
import { handleApiError, performLogout } from '../utils/errorHandler';
import { newAddressProp, User } from '../user/types/type';
import { City } from '../conf/type';

axios.interceptors.response.use(
    (response) => {
        if (response.data) {
            const errorMessage = response.data.errorMessage || response.data.error;
            if (errorMessage) {
                const apiError = handleApiError({ message: errorMessage });
                if (apiError.shouldLogout) {
                    performLogout();
                    return Promise.reject(new Error(apiError.message));
                }
            }
        }
        return response;
    },
    (error) => {
        const apiError = handleApiError(error);
        if (apiError.shouldLogout) {
            performLogout();
        }
        return Promise.reject(error);
    }
);

const API_DOMAIN = import.meta.env.VITE_API_DOMAIN;
const API_PORT = import.meta.env.VITE_API_PORT;

const API_URL = `${API_DOMAIN}:${API_PORT}`;
const DEVICE_TOKEN = import.meta.env.VITE_DEVICE_TOKEN;

const dataProvider: DataProvider = {
  login: async ({ username, password }: FormDataLogin) => {
    const response = await axios.post(
      `${API_URL}/Administration/Login`,
      { username, password },
      { withCredentials: true }
    );
    if (response.data.errorMessage) {
      throw new Error(response.data.errorMessage);
    }
    if (response.data.error) {
      throw new Error(response.data.error);
    }
    if (response.data.sessionToken == null) {
      throw new Error('Session token not found');
    }
    localStorage.setItem('sessionToken', response.data.sessionToken);
    return response.data;
  },

  logout: async () => {
    const sessionToken = localStorage.getItem('sessionToken');
    if (!sessionToken) {
      return Promise.resolve();
    }
    const response = await axios.get(`${API_URL}/Administration/Logout`, { withCredentials: true });
    if (response.data.errorMessage) {
      throw new Error(response.data.errorMessage);
    }
    if (response.data.error) {
      throw new Error(response.data.error);
    }
    localStorage.removeItem('sessionToken');
    return Promise.resolve();
  },

  checkAuth: async () => {
    const sessionToken = localStorage.getItem('sessionToken');
    if (!sessionToken) {
      throw new Error('No session token found');
    }
    const response = await axios.get(`${API_URL}/Administration/Details`, { withCredentials: true });
    if (response.data.errorMessage) {
      localStorage.removeItem('sessionToken');
      throw new Error(response.data.errorMessage);
    }
    if (response.data.error) {
      localStorage.removeItem('sessionToken');
      throw new Error(response.data.error);
    }
    const currentAdminId = response.data.administratorId || response.data.id;
    localStorage.setItem('currentAdminId', currentAdminId.toString());
    return Promise.resolve();
  },

  checkError: (error: any) => {
    const apiError = handleApiError(error);
    
    if (apiError.shouldLogout || error.response?.status === 401) {
        performLogout();
        throw new Error('Unauthorized');
    }
    return Promise.resolve();
},

  getPermissions: () => Promise.resolve(),

  getList: async (resource: string, params: any) => {
    const { page, perPage } = params.pagination || { page: 0, perPage: 100 };
    const { filter } = params.filter || {};

    const response = await axios.get(`${API_URL}/Administration/ManageQueryAdmins`, {
      params: { filter: filter || '', page, count: perPage },
      withCredentials: true,
    });

    if (response.data.errorMessage) {
      throw new Error(response.data.errorMessage);
    }
    if (response.data.error) {
      throw new Error(response.data.error);
    }

    const data = response.data.accounts || [];
    return {
      data: data.map((admin: any) => ({
        ...admin,
        id: admin.administratorId,
        profileImageUrl: admin.profileImageUrl !== "" ? `${API_URL}${admin.profileImageUrl}` : "",
        profileThumbnailUrl: admin.profileThumbnailUrl !== "" ? `${API_URL}${admin.profileThumbnailUrl}` : ""
      })),
      total: data.length,
    };
  },

  //@ts-ignore
  getOne: () => Promise.resolve({ data: {} }),
  getMany: () => Promise.resolve({ data: [] }),
  getManyReference: () => Promise.resolve({ data: [], total: 0 }),
  updateMany: () => Promise.resolve({ data: [] }),
  //@ts-ignore
  delete: async (resource: string, params: any) => {
    const { id } = params;
    const response = await axios.get(`${API_URL}/Administration/ManageDelete`, {
      params: { administratorId: id },
      withCredentials: true,
    });

    if (response.data.errorMessage) {
      throw new Error(response.data.errorMessage);
    }
    if (response.data.error) {
      throw new Error(response.data.error);
    }

    return { data: { id } };
  },
  deleteMany: () => Promise.resolve({ data: [] }),
};

export interface FormDataLogin {
  username: string;
  password: string;
}

export default dataProvider;